import React, { useState } from 'react';
import { Button } from '@wds/button';
import { Typography } from '@wds/typography';
import { ProgressCircle } from '@wds/progress-circle';
import { Modal } from '@wds/modal';
import { getCookie } from 'sma.common/utils';

// ---------------------------------------------------------------------------
// Shared modal shell
// ---------------------------------------------------------------------------

export const ModalOverlay = ({ onClose, children }) => (
    <div
        style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 2000,
        }}
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
        <div style={{
            background: '#fff', borderRadius: 8,
            boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
            width: '100%', maxWidth: 520, margin: 16, position: 'relative',
        }}>
            {children}
        </div>
    </div>
);

export const ModalHeader = ({ title, onClose }) => (
    <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '20px 24px 16px', borderBottom: '1px solid #e4e8eb',
    }}>
        <Typography variant='body' weight='bold' style={{ fontSize: 18, color: '#1b2a32', margin: 0 }}>
            {title}
        </Typography>
        <button
            type='button'
            onClick={onClose}
            aria-label={gettext('Close')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#637381', lineHeight: 1, padding: 4 }}
        >
            &times;
        </button>
    </div>
);

// ---------------------------------------------------------------------------
// Consent / authorization modal (shown when no site connections exist)
// ---------------------------------------------------------------------------

export const ConsentModal = ({ onProceed, onClose }) => {
    const [authorized, setAuthorized] = useState(false);

    return (
        <ModalOverlay onClose={onClose}>
            <ModalHeader title={gettext('Connect Docusign')} onClose={onClose} />
            <div style={{ padding: '20px 24px' }}>
                <Typography variant='body' style={{ fontSize: 14, color: '#1b2a32', lineHeight: '1.6', marginBottom: 16 }}>
                    {gettext(
                        'By connecting Docusign, you authorize SurveyMonkey Apply to send form data, '
                        + 'generated documents, signer information, and signature status information to '
                        + 'Docusign to process signature requests. Docusign may process this information '
                        + 'in regions outside your location.',
                    )}
                    <br /><br />
                    {gettext(
                        'Your organization is responsible for its Docusign account, including any terms, '
                        + 'subscriptions, permissions, envelope usage, fees, and applicant notices or consents.',
                    )}
                </Typography>

                <label style={{
                    display: 'flex', alignItems: 'flex-start', gap: 10,
                    cursor: 'pointer', marginBottom: 24,
                }}>
                    <input
                        type='checkbox'
                        checked={authorized}
                        onChange={(e) => setAuthorized(e.target.checked)}
                        style={{ marginTop: 2, flexShrink: 0, width: 18, height: 18, accentColor: '#1D9A5C', cursor: 'pointer' }}
                    />
                    <Typography variant='body' style={{ fontSize: 14, color: '#1b2a32' }}>
                        {gettext("I am authorized to connect my organization's Docusign account.")}
                    </Typography>
                </label>

                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        color='success'
                        disabled={!authorized}
                        onClick={onProceed}
                        style={{ minWidth: 120 }}
                    >
                        {gettext('Proceed')}
                    </Button>
                </div>
            </div>
        </ModalOverlay>
    );
};

// ---------------------------------------------------------------------------
// Account picker modal (shown when site already has connections)
// ---------------------------------------------------------------------------

const RadioCircle = ({ selected }) => (
    <svg width='18' height='18' viewBox='0 0 18 18' aria-hidden='true' style={{ flexShrink: 0, marginTop: 1 }}>
        <circle cx='9' cy='9' r='8' fill='none' stroke={selected ? '#1D9A5C' : '#C4CDD5'} strokeWidth='1.5' />
        {selected && <circle cx='9' cy='9' r='4.5' fill='#1D9A5C' />}
    </svg>
);

export const AccountPickerModal = ({
    connections, onContinueWithExisting, onSignWithNew, onClose, assigning,
}) => {
    const [useNew, setUseNew] = useState(false);
    const [selectedId, setSelectedId] = useState(connections[0]?.id ?? null);

    const handleContinue = () => {
        if (useNew) {
            onSignWithNew();
        } else {
            onContinueWithExisting(selectedId);
        }
    };

    return (
        <ModalOverlay onClose={onClose}>
            <ModalHeader title={gettext('Select Account')} onClose={onClose} />
            <div style={{ padding: '20px 24px' }}>
                <Typography variant='body' style={{ fontSize: 14, color: '#637381', marginBottom: 20 }}>
                    {gettext("Choose how you'd like to connect your Docusign account to get started.")}
                </Typography>

                <label style={{
                    display: 'flex', alignItems: 'flex-start', gap: 12,
                    padding: '14px 16px', cursor: 'pointer',
                    border: '1px solid', borderRadius: 6, marginBottom: 10,
                    borderColor: !useNew ? '#1D9A5C' : '#e4e8eb',
                    background: !useNew ? '#f0faf5' : '#fff',
                }}>
                    <input type='radio' name='ds-picker-promo' checked={!useNew} onChange={() => setUseNew(false)} style={{ display: 'none' }} />
                    <RadioCircle selected={!useNew} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <Typography variant='body' weight='bold' style={{ fontSize: 14, color: '#1b2a32' }}>
                            {gettext('Continue with your existing account(s)')}
                        </Typography>
                        <Typography variant='body' style={{ fontSize: 13, color: '#637381', marginBottom: 8 }}>
                            {gettext('Authorize the integration using your current Docusign session.')}
                        </Typography>
                        {!useNew && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Typography variant='body' style={{ fontSize: 13, color: '#637381', flexShrink: 0 }}>
                                    {gettext('Signed in with')}
                                </Typography>
                                <select
                                    value={selectedId ?? ''}
                                    onChange={(e) => setSelectedId(Number(e.target.value))}
                                    style={{
                                        flex: 1, padding: '6px 10px', border: '1px solid #C4CDD5',
                                        borderRadius: 4, fontSize: 13, color: '#1b2a32', background: '#fff',
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {connections.map((c) => (
                                        <option key={c.id} value={c.id}>
                                            {c.display_name || c.account_email}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </div>
                </label>

                <label style={{
                    display: 'flex', alignItems: 'flex-start', gap: 12,
                    padding: '14px 16px', cursor: 'pointer',
                    border: '1px solid', borderRadius: 6, marginBottom: 24,
                    borderColor: useNew ? '#1D9A5C' : '#e4e8eb',
                    background: useNew ? '#f0faf5' : '#fff',
                }}>
                    <input type='radio' name='ds-picker-promo' checked={useNew} onChange={() => setUseNew(true)} style={{ display: 'none' }} />
                    <RadioCircle selected={useNew} />
                    <div>
                        <Typography variant='body' weight='bold' style={{ fontSize: 14, color: '#1b2a32' }}>
                            {gettext('Sign in with a new account')}
                        </Typography>
                        <Typography variant='body' style={{ fontSize: 13, color: '#637381' }}>
                            {gettext('Use another Docusign account to authorize the integration.')}
                        </Typography>
                    </div>
                </label>

                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        color='success'
                        onClick={handleContinue}
                        disabled={assigning}
                        style={{ minWidth: 120 }}
                    >
                        {assigning
                            ? <><ProgressCircle continuous size='sm' color='light' />&nbsp;{gettext('Saving...')}</>
                            : gettext('Continue')}
                    </Button>
                </div>
            </div>
        </ModalOverlay>
    );
};

// ---------------------------------------------------------------------------
// Finish-setup modal — shown after a Docusign account is connected/assigned,
// confirming the connection and the (default-on) manual-fallback behaviour.
// Replaces the old inline dismissible note and, before that, the old
// FallbackPreferenceModal choice prompt. Manual fallback defaults to on;
// admins who want it off use the toggle on the Docusign connection detail
// (settings) page, not this modal.
// ---------------------------------------------------------------------------

export const DocuSignFinishSetupModal = ({ accountName, onClose }) => (
    <Modal size='md' show onClose={onClose} id='sma-docusign-finish-setup-modal'>
        <Modal.Header header={gettext('Docusign connected')} />
        <Modal.Body>
            <Typography variant='body' style={{ fontSize: 14, color: '#1b2a32', marginBottom: 12 }}>
                {accountName
                    ? interpolate(gettext('Your Docusign account %(name)s is connected.'), { name: accountName }, true)
                    : gettext('Your Docusign account is connected.')}
            </Typography>
            <Typography variant='body' style={{ fontSize: 13, color: '#637381' }}>
                {gettext(
                    "If your Docusign account encounters an issue, we'll automatically switch to "
                    + 'manual signing so documents can continue moving. You can manage this anytime '
                    + 'in Docusign Integration Settings.',
                )}
            </Typography>
        </Modal.Body>
        <Modal.Footer align='right'>
            <Button color='success' onClick={onClose}>
                {gettext('Finish Setup')}
            </Button>
        </Modal.Footer>
    </Modal>
);

// ---------------------------------------------------------------------------
// Upgrade modal (shown when plan doesn't include Docusign) — sends an
// upgrade-request email to the internal sales/support team on "Send request".
// ---------------------------------------------------------------------------

export const UpgradeModal = ({ upgradeUrl, onClose }) => {
	const [state, setState] = useState('idle'); // idle | sending | sent | error

	const handleSendRequest = async () => {
		if (!upgradeUrl) return;
		setState('sending');
		try {
			const resp = await fetch(upgradeUrl, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'X-CSRFToken': getCookie('csrftoken'),
				},
				body: JSON.stringify({}),
			});
			setState(resp.ok ? 'sent' : 'error');
		} catch (_e) {
			setState('error');
		}
	};

	return (
		<Modal size='md' show onClose={onClose} id='sma-docusign-upgrade-modal'>
			<Modal.Header header={gettext('Add Docusign to your plan')} />
			<Modal.Body>
				<Typography variant='body' style={{ fontSize: 14, color: '#637381' }}>
					{gettext('Send a request to upgrade your plan and get access.')}
				</Typography>
				{state === 'sent' && (
					<Typography variant='body' style={{ fontSize: 13, color: '#1D9A5C', marginTop: 12 }}>
						{gettext('Your request has been sent.')}
					</Typography>
				)}
				{state === 'error' && (
					<Typography variant='body' style={{ fontSize: 13, color: '#D82C0D', marginTop: 12 }}>
						{gettext('Failed to send request. Please try again.')}
					</Typography>
				)}
			</Modal.Body>
			<Modal.Footer align='right'>
				<Button onClick={onClose} variant='text' color='primary'>
					{gettext('Close')}
				</Button>
				<Button
					color='success'
					onClick={handleSendRequest}
					disabled={state === 'sending' || state === 'sent'}
				>
					{state === 'sending'
						? <><ProgressCircle continuous size='sm' color='light' />&nbsp;{gettext('Sending…')}</>
						: gettext('Send request')}
				</Button>
			</Modal.Footer>
		</Modal>
	);
};
